const EDIT_VISUAL_BLOCK_COUNT_FIELDS = [
	'imageBlocks',
	'mauiAppBlocks',
	'addedImageBlocks',
	'replacedImageBlocks',
	'removedImageBlocks',
	'addedMauiAppBlocks',
	'replacedMauiAppBlocks',
	'removedMauiAppBlocks',
] as const;

/**
 * Counts of generated visuals placed by a page edit, set by the backend on the terminal
 * `aifc-adf-update` payload only. `imageBlocks` / `mauiAppBlocks` are added + replaced; `removed*`
 * counts existing visuals the edit deleted without a replacement. Either all eight are present or
 * none are: absent means not eligible or unknown (feature off, fast-path or headless edit, older
 * backend).
 */
export type GeneratedEditVisualBlockCounts = {
	[Field in (typeof EDIT_VISUAL_BLOCK_COUNT_FIELDS)[number]]?: number;
};

const isBlockCount = (value: unknown): value is number =>
	typeof value === 'number' && Number.isInteger(value) && value >= 0;

/**
 * Reads the generated visual block counts from page-edit action data for analytics attributes.
 * The eight fields are taken together or not at all, and are never defaulted, so `0` and unknown
 * stay distinct and a malformed payload cannot report a partial set.
 */
export const getGeneratedEditVisualBlockCounts = (
	data: unknown,
): GeneratedEditVisualBlockCounts => {
	if (typeof data !== 'object' || data === null) {
		return {};
	}
	const record = data as Record<string, unknown>;
	if (!EDIT_VISUAL_BLOCK_COUNT_FIELDS.every((field) => isBlockCount(record[field]))) {
		return {};
	}
	return Object.fromEntries(
		EDIT_VISUAL_BLOCK_COUNT_FIELDS.map((field) => [field, record[field]]),
	) as GeneratedEditVisualBlockCounts;
};
